import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import { Calendar, ChevronDown, ChevronRight, Search } from "lucide-react";
import { AppSidebarHeader, HeaderIconPlaceholder, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { cn } from "@repo/blacksmith-ui/cn";
import { SidebarPrimaryNavStatic } from "./SidebarPrimaryNav";
import { MY_SESSIONS_HEADER_MIN_HEIGHT_CLASS, REVEAL_ON_SIDEBAR_HOVER_CLASS, SESSION_FOOTER_CLASS, SESSION_FOOTER_LABEL_CLASS, SESSION_ITEM_INDICATOR_GUTTER_CLASS, SESSION_ITEM_ROW_CLASS, SESSION_ITEM_ROW_WRAP_CLASS, SESSION_LIST_SCROLLER_CLASS, SESSION_SECTION_HEADER_BOX_CLASS, isSessionListScrolledToBottom } from "./sessionSidebarChrome";
import { readLeftSidebarSkeletonLayout, readSkeletonAutomationRows, readStoredLayoutValue, useIsHydrated } from "./skeletonLayout";
import { AUTOMATIONS_EXPANDED_STORAGE_KEY } from "./useCachedSidebarAutomationRows";
export const SESSION_SIDEBAR_EDGE_CLASS = "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-20 after:w-px after:bg-[var(--color-border)]"; // above children: the opaque session list would cover an inset shadow

interface SidebarSkeletonProps {
  variant?: "full" | "inline";
  /** Automations group rows (`null`: no section); defaults to the URL org's last visit. */
  automationRows?: number | null;
  /** Mirrors SessionSidebar's `showSearch` so the header doesn't shift on load. */
  showSearch?: boolean;
}
const TITLE_WIDTHS = ["w-[78%]", "w-[55%]", "w-[68%]", "w-[44%]", "w-[62%]"];
const AUTOMATION_TITLE_WIDTHS = ["w-[60%]", "w-[48%]", "w-[54%]", "w-[42%]"];
function SessionItemSkeleton(t0) {
  const $ = _c(11);
  const {
    showAuthor,
    titleWidth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `h-3.5 ${titleWidth} rounded`;
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="flex h-[22px] items-center"><Skeleton className={t2} /></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== showAuthor) {
    t4 = showAuthor && <Skeleton className="h-2.5 w-10 rounded" />;
    $[3] = showAuthor;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-2.5 w-12 rounded" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4) {
    t6 = <div className="flex h-3 items-center gap-2">{t4}{t5}</div>;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <div className={SESSION_ITEM_ROW_WRAP_CLASS}><div className={SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="min-w-0 flex-1 overflow-hidden">{t3}{t6}</div></div></div>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}

/** Collapsed AutomationSessionGroupItem header: chevron gutter, name, recency + calendar. */
function AutomationGroupSkeleton(t0) {
  const $ = _c(6);
  const {
    titleWidth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex w-4 shrink-0 items-center justify-center py-[5px]"><ChevronRight className="size-3.5 text-neutral-500 dark:text-neutral-400" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `h-3.5 ${titleWidth} rounded`;
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="flex h-[22px] items-center"><Skeleton className={t2} /></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex h-3 items-center gap-1.5"><Skeleton className="h-2.5 w-16 rounded" /><Calendar className="size-3 shrink-0 text-neutral-500 dark:text-neutral-400" /></div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <div className={SESSION_ITEM_ROW_CLASS} data-slot="automation-group-skeleton">{t1}<div className="min-w-0 flex-1 overflow-hidden">{t3}{t4}</div></div>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function SectionHeaderSkeleton(t0) {
  const $ = _c(17);
  const {
    label,
    first,
    minHeightClass,
    expanded: t1
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const t2 = first ? "pt-3" : "pt-5";
  let t3;
  if ($[0] !== t2) {
    t3 = cn(SESSION_SECTION_HEADER_BOX_CLASS, t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== minHeightClass) {
    t4 = cn("flex items-center", minHeightClass);
    $[2] = minHeightClass;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label) {
    t5 = <span className="type-uppercase-title text-neutral-500 dark:text-neutral-300">{label}</span>;
    $[4] = label;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = expanded ? REVEAL_ON_SIDEBAR_HOVER_CLASS : "rotate-180";
  let t8;
  if ($[9] !== t7) {
    t8 = cn("size-3 text-neutral-500 dark:text-neutral-400", t7);
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <ChevronDown className={t8} aria-hidden={true} />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t3 || $[14] !== t6 || $[15] !== t9) {
    t10 = <div className={t3}>{t6}{t9}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
function SidebarBodyContent(t0) {
  const $ = _c(9);
  const {
    automationRows,
    automationsExpanded
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SectionHeaderSkeleton label="My Sessions" first={true} minHeightClass={MY_SESSIONS_HEADER_MIN_HEIGHT_CLASS} />;
    t2 = TITLE_WIDTHS.map(_temp);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== automationRows || $[3] !== automationsExpanded) {
    t3 = automationRows !== null && <><SectionHeaderSkeleton label="Automations" first={false} expanded={automationsExpanded} />{Array.from({
        length: automationRows
      }, _temp2)}</>;
    $[2] = automationRows;
    $[3] = automationsExpanded;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SectionHeaderSkeleton label="Team Sessions" first={false} />;
    t5 = TITLE_WIDTHS.map(_temp3);
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[7] = t3;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function _temp3(w_0, i_1) {
  return <SessionItemSkeleton key={`team-${i_1}`} showAuthor={true} titleWidth={w_0} />;
}
function _temp2(_, i_0) {
  return <AutomationGroupSkeleton key={`automation-${i_0}`} titleWidth={AUTOMATION_TITLE_WIDTHS[i_0 % AUTOMATION_TITLE_WIDTHS.length]} />;
}
function _temp(w, i) {
  return <SessionItemSkeleton key={`my-${i}`} titleWidth={w} />;
}
function SidebarBody(t0) {
  const $ = _c(11);
  const {
    automationRows,
    automationsExpanded
  } = t0;
  const scrollerRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = scrollerRef.current;
      if (!el) {
        return;
      }
      const measure = () => setScrolledToBottom(isSessionListScrolledToBottom(el));
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      for (const child of Array.from(el.children)) {
        observer.observe(child);
      }
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="px-2 pt-1"><SidebarPrimaryNavStatic rows="subpages" /></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== automationRows || $[4] !== automationsExpanded) {
    t4 = <div ref={scrollerRef} className={SESSION_LIST_SCROLLER_CLASS}>{t3}<div className="px-2 pb-1"><SidebarBodyContent automationRows={automationRows} automationsExpanded={automationsExpanded} /></div></div>;
    $[3] = automationRows;
    $[4] = automationsExpanded;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== scrolledToBottom) {
    t5 = !scrolledToBottom && <div data-slot="session-list-bottom-fade" className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--sidebar)] to-transparent" />;
    $[6] = scrolledToBottom;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="flex-1 relative min-h-0">{t4}{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function SidebarSkeleton(t0) {
  const $ = _c(30);
  const {
    variant: t1,
    automationRows,
    showSearch: t2
  } = t0;
  const variant = t1 === undefined ? "full" : t1;
  const showSearch = t2 === undefined ? true : t2;
  const isHydrated = useIsHydrated();
  let t3;
  if ($[0] !== isHydrated || $[1] !== variant) {
    t3 = variant === "full" && isHydrated ? readLeftSidebarSkeletonLayout() : null;
    $[0] = isHydrated;
    $[1] = variant;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const layout = t3;
  let t4;
  if ($[3] !== automationRows || $[4] !== isHydrated) {
    t4 = automationRows !== undefined ? automationRows : isHydrated ? readSkeletonAutomationRows() : null;
    $[3] = automationRows;
    $[4] = isHydrated;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const resolvedAutomationRows = t4;
  let t5;
  if ($[6] !== isHydrated) {
    t5 = isHydrated ? readStoredLayoutValue(AUTOMATIONS_EXPANDED_STORAGE_KEY, true) : true;
    $[6] = isHydrated;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const automationsExpanded = t5;
  if (variant === "inline") {
    let t6;
    if ($[8] !== automationsExpanded || $[9] !== resolvedAutomationRows) {
      t6 = <SidebarBodyContent automationRows={resolvedAutomationRows} automationsExpanded={automationsExpanded} />;
      $[8] = automationsExpanded;
      $[9] = resolvedAutomationRows;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (layout && !layout.visible) {
    return null;
  }
  let t6;
  if ($[11] !== layout) {
    t6 = layout ? {
      width: layout.width
    } : undefined;
    $[11] = layout;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = layout ? "flex" : "hidden min-[1018px]:flex w-[330px]";
  let t8;
  if ($[13] !== t7) {
    t8 = cn("relative flex-col bg-sidebar overflow-hidden pointer-events-none flex-shrink-0", SESSION_SIDEBAR_EDGE_CLASS, t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== showSearch) {
    t9 = showSearch && <HeaderIconPlaceholder icon={Search} />;
    $[15] = showSearch;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <PanelTogglePlaceholder />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <AppSidebarHeader title="[code]smith" actions={<>{t9}{t10}</>} />;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarPrimaryNavStatic rows="new-session" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== automationsExpanded || $[22] !== resolvedAutomationRows) {
    t13 = <SidebarBody automationRows={resolvedAutomationRows} automationsExpanded={automationsExpanded} />;
    $[21] = automationsExpanded;
    $[22] = resolvedAutomationRows;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className={SESSION_FOOTER_CLASS}><span className={cn(SESSION_FOOTER_LABEL_CLASS, REVEAL_ON_SIDEBAR_HOVER_CLASS)}>Archived</span></div>;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t11 || $[26] !== t13 || $[27] !== t6 || $[28] !== t8) {
    t15 = <div style={t6} className={t8} data-slot="assistant-sidebar-skeleton">{t11}{t12}{t13}{t14}</div>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t6;
    $[28] = t8;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}